import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL} from '../config/constants'; ;

interface Accessory {
  _id: string;
  name: string;
  type: string;
  color: string;
  description?: string;
  images: string[];
  createdAt: string;
}

const AccessoryPool: React.FC = () => {
  const [accessories, setAccessories] = useState<Accessory[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filterType, setFilterType] = useState<string>('');

  const accessoryTypes = ['Düğme', 'Fermuar', 'Toka', 'Etiket', 'Astar', 'Diğer'];
  const apiUrl = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const fetchAccessories = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${apiUrl}/accessories`);
        setAccessories(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching accessories:', error);
        setError('Aksesuarları yüklerken bir hata oluştu.');
        setLoading(false);
      }
    };
    
    fetchAccessories();
  }, [apiUrl]);

  // Filtreleme fonksiyonu - Hata güvenliği eklenmiş
  const filteredAccessories = accessories.filter(accessory => {
    // Aksesuarın name özelliğinin null veya undefined olmadığından emin olun
    const nameMatches = accessory.name ? 
      accessory.name.toLowerCase().includes(searchTerm.toLowerCase()) : 
      false;
    
    // Tip filtresi kontrolü
    const typeMatches = filterType === '' || accessory.type === filterType;
    
    return nameMatches && typeMatches;
  });

  const handleDelete = async (id: string) => {
    if (window.confirm('Bu aksesuarı silmek istediğinizden emin misiniz?')) {
      try {
        await axios.delete(`${apiUrl}/accessories/${id}`);
        setAccessories(accessories.filter(accessory => accessory._id !== id));
      } catch (error) {
        console.error('Error deleting accessory:', error);
        setError('Aksesuar silinirken bir hata oluştu.');
      }
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Aksesuar Havuzu</h1>
        <Link 
          to="/accessories/create" 
          className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2.5 rounded-md shadow-sm transition duration-200 flex items-center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Yeni Aksesuar Ekle
        </Link>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex flex-col md:flex-row gap-4 mb-4">
          <div className="md:w-1/2">
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">Aksesuar Ara</label>
            <input
              type="text"
              id="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="İsme göre ara..."
              className="w-full px-4 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="md:w-1/2">
            <label htmlFor="filterType" className="block text-sm font-medium text-gray-700 mb-1">Aksesuar Tipine Göre Filtrele</label>
            <select
              id="filterType"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="">Tümü</option>
              {accessoryTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Yükleniyor...</p>
        </div>
      ) : error ? (
        <div className="bg-red-50 p-4 rounded-md text-red-800">
          <p>{error}</p>
        </div>
      ) : filteredAccessories.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500">Kayıtlı aksesuar bulunamadı.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredAccessories.map((accessory) => (
            <div key={accessory._id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300">
              <div className="relative h-48 overflow-hidden">
                {accessory.images && accessory.images.length > 0 ? (
                  <img
                    src={API_URL+accessory.images[0]}
                    alt={accessory.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = '/placeholder-accessory.png';
                    }}
                  />
                ) : (
                  <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                    <span className="text-gray-400">Görsel yok</span>
                  </div>
                )}
                <div 
                  className="absolute top-0 right-0 mt-2 mr-2 w-8 h-8 rounded-full border-2 border-white shadow-sm" 
                  style={{ backgroundColor: accessory.color }}
                  title={accessory.color}
                ></div>
              </div>
              
              <div className="p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-xl font-semibold mb-1">{accessory.name || 'İsimsiz Aksesuar'}</h2>
                    <p className="text-sm text-gray-500 mb-2">Tip: {accessory.type || 'Belirtilmemiş'}</p>
                  </div>
                </div>
                
                {accessory.description && (
                  <p className="text-gray-700 text-sm mt-2 line-clamp-2">{accessory.description}</p>
                )}

                <div className="mt-4 pt-3 border-t border-gray-100 flex justify-between">
                  <Link 
                    to={`/accessories/${accessory._id}`} 
                    className="text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Detaylar
                  </Link>
                  <div className="flex space-x-3">
                    <Link 
                      to={`/accessories/${accessory._id}/edit`} 
                      className="text-gray-600 hover:text-gray-800"
                    >
                      Düzenle
                    </Link>
                    <button 
                      onClick={() => handleDelete(accessory._id)} 
                      className="text-red-600 hover:text-red-800"
                    >
                      Sil
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AccessoryPool;
